import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X, XCircle, Timer, EyeOff, Activity, ChevronDown, Wrench, Cpu, SearchX, CircleSlash, AlertTriangle, Percent,
  Hourglass, Inbox, Repeat, RotateCcw, MessagesSquare, MessageCircleQuestion, LogOut, Clock, Gauge, KeyRound,
  ServerCrash, Unplug, Code2, FileWarning, Ban, Search, CircleHelp,
} from 'lucide-react';
import SlackChannelPicker from 'features/agents/WorkflowEditor/SlackChannelPicker';
import { formatRelativeTime } from 'features/agent-analytics/components/runCells';
import {
  getTracking, deployTracker, updateTracker, setTrackerLive, listTeamAgents, errorText,
} from 'features/issues/api/issuesApi';
import useBillingV2Status from 'shared/hooks/useBillingV2Status';
import DrawerTab from 'shared/components/ui/DrawerTab';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';

/**
 * Track issues: the issue types as templates. Each is a row; opening one
 * shows its settings below it, and deploying sets up everything it needs at
 * once — its alert with these thresholds (and, for a silent failure, the eval
 * that catches it) and the agent that has Ask file or attach issues when the
 * alert fires. Each piece is an ordinary alert, eval and agent, edited where
 * it lives.
 */

const TABS = [
  { id: 'failures', label: 'Hard failures', icon: XCircle },
  { id: 'latency', label: 'Latency', icon: Timer },
  { id: 'silent', label: 'Silent failures', icon: EyeOff },
  { id: 'tracking', label: 'Tracking', icon: Activity },
];

/** A face per template; the tab's icon when a new one arrives unknown. */
const ICON = {
  hard_failures: XCircle,
  tool_call_failures: Wrench,
  model_call_failures: Cpu,
  retrieval_failures: SearchX,
  run_failures: CircleSlash,
  runs_with_failed_steps: AlertTriangle,
  error_rate: Percent,
  timeouts: Clock,
  rate_limits: Gauge,
  auth_errors: KeyRound,
  server_errors: ServerCrash,
  connection_errors: Unplug,
  code_errors: Code2,
  bad_requests: FileWarning,
  not_found: CircleHelp,
  refused: Ban,
  slow_runs: Timer,
  slow_tool: Hourglass,
  silent_empty: Inbox,
  empty_retrieval: Search,
  tool_loops: Repeat,
  retry_exhaustion: RotateCcw,
  model_call_health: Cpu,
  user_repeats: MessagesSquare,
  unanswered: MessageCircleQuestion,
  user_drop_off: LogOut,
};
const LEVEL = { span: 'Span', run: 'Run', eval: 'Eval + alert' };
const iconOf = (t) => ICON[t.id] || TABS.find((x) => x.id === t.category)?.icon || Activity;

const INPUT = 'h-8 rounded-md border border-[color:var(--border-default)] bg-surface px-2 text-[12px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]';
const LABEL = 'text-[11px] font-medium text-[color:var(--text-muted)]';
const LINK = 'underline-offset-2 hover:text-[color:var(--text-primary)] hover:underline';

function Switch({ on, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-[color:var(--trodo-primary)]' : 'bg-[color:var(--border-default)]'}`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
    </button>
  );
}

function Icon({ as: I }) {
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[color:var(--text-muted)]">
      <I size={16} strokeWidth={1.75} aria-hidden />
    </span>
  );
}

const windowLabel = (windows, s) => (windows.find((w) => w.seconds === s)?.label || `${Math.round(s / 60)} min`);

/** The settings for one template, opened under its row. */
function DeployForm({ teamId, template, windows, agents, tracker, onCancel, onDeployed }) {
  const [agent, setAgent] = useState(tracker?.agent || '');
  const [threshold, setThreshold] = useState(tracker ? tracker.alert.threshold : template.defaults.threshold);
  const [windowSeconds, setWindowSeconds] = useState(tracker ? tracker.alert.window_seconds : template.defaults.window_seconds);
  const [minSample, setMinSample] = useState(tracker ? tracker.alert.min_sample : template.defaults.min_sample);
  const [channel, setChannel] = useState(tracker?.slack_channel_id || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const body = {
        threshold: Number(threshold),
        window_seconds: Number(windowSeconds),
        min_sample: Number(minSample),
        slack_channel_id: channel || null,
      };
      const t = tracker
        ? await updateTracker(teamId, tracker.id, body)
        : await deployTracker(teamId, { template: template.id, agent: agent || null, ...body });
      onDeployed(t);
    } catch (err) {
      setError(errorText(err, tracker ? 'Could not save' : 'Could not deploy'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 border-t border-[color:var(--border-subtle)] px-3 pb-3 pt-3" data-testid="deploy-form">
      {tracker ? null : <p className="text-[11.5px] text-[color:var(--text-muted)]">
        {template.kind === 'eval'
          ? 'Deploys an eval that catches it, an alert on its failures, and an agent: when the alert fires, Lucid files or attaches issues.'
          : 'Deploys an alert and an agent: when the alert fires, Lucid files or attaches issues.'}
      </p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className={LABEL}>Agent</span>
          <select value={agent} onChange={(e) => setAgent(e.target.value)} disabled={Boolean(tracker)} className={`${INPUT} disabled:opacity-60`} aria-label="Agent">
            <option value="">All agents</option>
            {tracker?.agent && !agents.some((a) => a.agent_name === tracker.agent) ? <option value={tracker.agent}>{tracker.agent}</option> : null}
            {agents.map((a) => <option key={a.agent_name} value={a.agent_name}>{a.agent_name}</option>)}
          </select>
        </label>
        <div className="flex flex-col gap-1">
          <span className={LABEL}>Fire at</span>
          <div className="flex items-center gap-2">
            <input type="number" min="0" step="any" value={threshold} onChange={(e) => setThreshold(e.target.value)} className={`${INPUT} w-20`} aria-label="Threshold" required />
            <span className="min-w-0 truncate text-[12px] text-[color:var(--text-secondary)]">{template.unit}</span>
          </div>
        </div>
        <label className="flex flex-col gap-1">
          <span className={LABEL}>Within</span>
          <select value={windowSeconds} onChange={(e) => setWindowSeconds(Number(e.target.value))} className={INPUT} aria-label="Window">
            {windows.map((w) => <option key={w.seconds} value={w.seconds}>{w.label}</option>)}
          </select>
        </label>
        {template.has_min_sample ? (
          <label className="flex flex-col gap-1">
            <span className={LABEL}>Only with at least</span>
            <div className="flex items-center gap-2">
              <input type="number" min="1" step="1" value={minSample} onChange={(e) => setMinSample(e.target.value)} className={`${INPUT} w-20`} aria-label="Minimum sample" />
              <span className="text-[12px] text-[color:var(--text-secondary)]">in the window</span>
            </div>
          </label>
        ) : null}
      </div>
      <SlackChannelPicker
        field={{ key: 'channel', label: 'Post to Slack (optional)' }}
        nodeKind="slack_send_message"
        showHelp={false}
        value={channel}
        onChange={(v) => setChannel(v || '')}
      />
      {error ? <div className="rounded-md border border-[color:var(--feedback-error)] p-2 text-[12px] text-[color:var(--feedback-error)]" role="alert">{error}</div> : null}
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="inline-flex h-8 items-center rounded-md border border-[color:var(--border-default)] px-3 text-[12px] font-medium text-[color:var(--text-secondary)] hover:bg-surface-hover">Cancel</button>
        <button type="submit" disabled={busy} className="inline-flex h-8 items-center rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover disabled:opacity-50">
          {tracker ? (busy ? 'Saving…' : 'Save') : (busy ? 'Deploying…' : 'Deploy')}
        </button>
      </div>
    </form>
  );
}

export default function TrackIssuesModal({ teamId, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('failures');
  const [openId, setOpenId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(null);
  const { status: billing } = useBillingV2Status(getSelectedTeamNumericId());
  const stopped = Boolean(billing?.executions?.stopped);

  const load = useCallback(() => getTracking(teamId)
    .then(setData)
    .catch((e) => setError(errorText(e, 'Could not load'))), [teamId]);

  useEffect(() => {
    load();
    listTeamAgents(teamId).then(setAgents).catch(() => setAgents([]));
  }, [teamId, load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const templates = useMemo(() => data?.templates || [], [data]);
  const trackers = useMemo(() => data?.trackers || [], [data]);
  const byId = useMemo(() => new Map(templates.map((t) => [t.id, t])), [templates]);
  const countFor = (id) => trackers.filter((t) => t.template === id).length;
  const q = query.trim().toLowerCase();
  const shownTemplates = templates.filter((t) => (q ? `${t.name} ${t.description}`.toLowerCase().includes(q) : t.category === tab));
  const counts = { tracking: trackers.length };
  for (const t of templates) counts[t.category] = (counts[t.category] || 0) + 1;

  const toggle = async (tr, live) => {
    setBusy(tr.id);
    setError('');
    try {
      await setTrackerLive(teamId, tr.id, live);
      await load();
    } catch (e) {
      setError(errorText(e, 'That did not work'));
    } finally {
      setBusy(null);
    }
  };

  const open = (path) => { onClose(); navigate(`/${encodeURIComponent(teamId)}${path}`); };

  const el = (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[7vh]"
      style={{ background: 'color-mix(in srgb, var(--text-primary) 40%, transparent)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="track-issues-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* One height whichever tab is open, so the dialog never jumps. */}
      <div className="flex h-[min(680px,84vh)] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-2xl" style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="flex items-center gap-2 px-4 pt-3">
          <h2 id="track-issues-title" className="text-[14px] font-semibold text-[color:var(--text-primary)]">Track issues</h2>
          <div className="flex-1" />
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"><X size={16} /></button>
        </div>

        <div className="flex gap-1 overflow-x-auto border-b border-[color:var(--border-subtle)] px-4" role="tablist">
          {TABS.map((t) => (
            <DrawerTab
              key={t.id}
              id={t.id}
              role="tab"
              label={<><t.icon size={13} aria-hidden />{t.label}</>}
              badge={counts[t.id] || 0}
              active={tab === t.id}
              onClick={(id) => { setTab(id); setOpenId(null); setEditId(null); }}
            />
          ))}
        </div>

        <div className="px-4 pt-3">
          <label className="flex h-8 items-center gap-2 rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 focus-within:border-[color:var(--border-strong)]">
            <Search size={14} className="shrink-0 text-[color:var(--text-muted)]" aria-hidden />
            <input value={query} onChange={(e) => { setQuery(e.target.value); setOpenId(null); }} placeholder="Search issue types…" aria-label="Search issue types"
              className="min-w-0 flex-1 bg-transparent text-[12px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]" />
            {query ? <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><X size={13} /></button> : null}
          </label>
        </div>

        {stopped ? (
          <div className="border-b border-[color:var(--border-subtle)] bg-[color:var(--feedback-error-bg)] px-4 py-2 text-[11.5px] text-[color:var(--feedback-error)]">
            Paused: this period’s Executions are used up. Alerts still fire, but agents and evals won’t run until the next period or an upgrade.
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-3">
          {error ? <div className="mb-3 rounded-md bg-[color:var(--feedback-error-bg)] p-2 text-[12px] text-[color:var(--feedback-error)]" role="alert">{error}</div> : null}
          {!data && !error ? <div className="py-10 text-center text-[12px] text-[color:var(--text-muted)]">Loading…</div> : null}

          {data && (q || tab !== 'tracking') ? (
            <ul className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface" data-testid="track-templates">
              {shownTemplates.length === 0 ? <li className="py-8 text-center text-[12px] text-[color:var(--text-muted)]">No issue type matches.</li> : null}
              {shownTemplates.map((t) => {
                const n = countFor(t.id);
                const expanded = openId === t.id;
                return (
                  <li key={t.id} className="border-b border-[color:var(--border-subtle)] last:border-b-0">
                    <button
                      type="button"
                      onClick={() => setOpenId(expanded ? null : t.id)}
                      aria-expanded={expanded}
                      data-testid={`track-${t.id}`}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]"
                    >
                      <Icon as={iconOf(t)} />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-baseline gap-x-2">
                          <span className="text-[13px] font-medium text-[color:var(--text-primary)]">{t.name}</span>
                          <span className="text-[12px] text-[color:var(--text-secondary)]">{t.description}</span>
                        </span>
                      </span>
                      <span className="hidden shrink-0 text-[11px] text-[color:var(--text-muted)] sm:inline">{LEVEL[t.level] || t.level}</span>
                      {n ? <span className="shrink-0 text-[11px] font-medium text-[color:var(--feedback-success)]">Tracking {n}</span> : null}
                      <ChevronDown size={15} className={`shrink-0 text-[color:var(--text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden />
                    </button>
                    {expanded ? (
                      <DeployForm
                        teamId={teamId}
                        template={t}
                        windows={data.windows}
                        agents={agents}
                        onCancel={() => setOpenId(null)}
                        onDeployed={async () => { setOpenId(null); setTab('tracking'); await load(); }}
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}

          {data && !q && tab === 'tracking' ? (
            trackers.length === 0 ? (
              <div className="py-10 text-center text-[12px] text-[color:var(--text-muted)]">Nothing tracked yet. Open a template to deploy one.</div>
            ) : (
              <ul className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface" data-testid="trackers">
                {trackers.map((tr) => {
                  const t = byId.get(tr.template);
                  return (
                    <li key={tr.id} className="border-b border-[color:var(--border-subtle)] last:border-b-0">
                    <div className="flex items-center gap-3 px-3 py-2">
                      <Icon as={t ? iconOf(t) : Activity} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[12.5px] font-medium text-[color:var(--text-primary)]">
                          {t?.name || tr.name}
                          <span className="font-normal text-[color:var(--text-muted)]">{` · ${tr.agent || 'All agents'}`}</span>
                        </div>
                        <div className="text-[11px] text-[color:var(--text-muted)]">
                          {`${tr.alert.threshold.toLocaleString()} ${t?.unit || ''} within ${windowLabel(data.windows, tr.alert.window_seconds)}`}
                          {tr.alert.last_triggered_at ? ` · last fired ${formatRelativeTime(tr.alert.last_triggered_at)}` : ''}
                          {' · '}
                          <button type="button" className={LINK} onClick={() => open(`/alerts/${tr.alert.id}`)}>alert</button>
                          {tr.eval ? <>{' · '}<button type="button" className={LINK} onClick={() => open(`/agent-analytics/evals?eval=${tr.eval.id}`)}>eval</button></> : null}
                          {tr.workflow ? <>{' · '}<button type="button" className={LINK} onClick={() => open(`/agents/${tr.workflow.id}`)}>agent</button></> : null}
                        </div>
                      </div>
                      {t ? <button type="button" onClick={() => setEditId(editId === tr.id ? null : tr.id)} aria-expanded={editId === tr.id} aria-label={`Edit ${t.name}`} className="shrink-0 text-[12px] font-medium text-[color:var(--text-secondary)] underline-offset-2 hover:text-[color:var(--text-primary)] hover:underline">Edit</button> : null}
                      <Switch on={tr.live} disabled={busy === tr.id} onChange={(on) => toggle(tr, on)} label={`Track ${t?.name || tr.name}`} />
                    </div>
                    {t && editId === tr.id ? (
                      <DeployForm
                        teamId={teamId}
                        template={t}
                        windows={data.windows}
                        agents={agents}
                        tracker={tr}
                        onCancel={() => setEditId(null)}
                        onDeployed={async () => { setEditId(null); await load(); }}
                      />
                    ) : null}
                    </li>
                  );
                })}
              </ul>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
  return createPortal(el, document.body);
}
